import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import authTranslations from './locales/auth';
// Import translation resources
import commonTranslations from './locales/common';
import dashboardTranslations from './locales/dashboard';
import logsTranslations from './locales/logs';
import playersTranslations from './locales/players';
import programsTranslations from './locales/programs';

// Function to extract language specific translations
const extractTranslations = (
  combinedTranslations: Record<string, any>,
  language: string,
): Record<string, any> => {
  const result: Record<string, any> = {};

  const processObject = (obj: Record<string, any>, target: Record<string, any>) => {
    Object.keys(obj).forEach(key => {
      if (obj[key] && typeof obj[key] === 'object') {
        // If the object has 'en' and 'ja' properties directly, extract the language value
        if (obj[key].en !== undefined && obj[key].ja !== undefined) {
          target[key] = obj[key][language];
        } else {
          // Otherwise recursively process the object
          target[key] = {};
          processObject(obj[key], target[key]);
        }
      } else {
        // For non-objects (shouldn't happen in our structure but just in case)
        target[key] = obj[key];
      }
    });
  };

  processObject(combinedTranslations, result);
  return result;
};

// Function to get the stored language setting
export const getStoredLanguage = (): string => {
  if (typeof window === 'undefined') return 'en';
  return localStorage.getItem('language') || 'en';
};

// Function to save the language setting
export const setStoredLanguage = (lang: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('language', lang);
};

// Function to change the language
export const changeLanguage = (lang: string): void => {
  i18n.changeLanguage(lang);
  setStoredLanguage(lang);
};

// Extract translations for each namespace and language
const resources = {
  en: {
    common: extractTranslations(commonTranslations, 'en'),
    programs: extractTranslations(programsTranslations, 'en'),
    players: extractTranslations(playersTranslations, 'en'),
    logs: extractTranslations(logsTranslations, 'en'),
    auth: extractTranslations(authTranslations, 'en'),
    dashboard: extractTranslations(dashboardTranslations, 'en'),
  },
  ja: {
    common: extractTranslations(commonTranslations, 'ja'),
    programs: extractTranslations(programsTranslations, 'ja'),
    players: extractTranslations(playersTranslations, 'ja'),
    logs: extractTranslations(logsTranslations, 'ja'),
    auth: extractTranslations(authTranslations, 'ja'),
    dashboard: extractTranslations(dashboardTranslations, 'ja'),
  },
};

// Initialize i18n
i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getStoredLanguage(),
    fallbackLng: 'ja',
    interpolation: {
      escapeValue: false, // Not needed when using React
    },
    // Split namespaces
    ns: ['common', 'programs', 'players', 'logs', 'auth', 'dashboard'],
    defaultNS: 'common',
  });

export default i18n;
